import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { GraduationCap, Search, Upload } from "lucide-react";
import { api, deviceFingerprint } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATES = ["Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal","Delhi","Chandigarh","Jammu and Kashmir","Ladakh","Puducherry","Andaman and Nicobar Islands","Dadra and Nagar Haveli","Daman and Diu","Lakshadweep"];

export default function Auth() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") || "login");
  const [step, setStep] = useState("form"); // form | otp | role | onboarding
  const [userId, setUserId] = useState(null);
  const [role, setRole] = useState(null);
  const [form, setForm] = useState({ full_name:"", email:"", phone:"+91", password:"", verification_method:"phone" });
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const { refresh, user } = useAuth();
  const nav = useNavigate();
  const next = params.get("next");

  useEffect(()=> { if (user && step==="form") { redirectAfterAuth(user); } }, [user]);

  const redirectAfterAuth = (u) => {
    if (!u.role) { setStep("role"); return; }
    nav(next || (u.role === "admin" ? "/admin" : u.role === "campus_ambassador" ? "/dashboard/ambassador" : "/dashboard/student"));
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.register({ ...form, device_fingerprint: deviceFingerprint() });
      setUserId(res.user_id);
      setStep("otp");
      toast.success("Account created! Verify with OTP 123456 (dev mode)");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.login({ email: form.email, password: form.password });
      localStorage.setItem("cs_token", res.token);
      await refresh();
      toast.success("Welcome back!");
      const me = await api.me();
      redirectAfterAuth(me.user);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.verifyOtp({ user_id: userId, otp });
      localStorage.setItem("cs_token", res.token);
      await refresh();
      setStep("role");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "OTP verification failed");
    } finally { setLoading(false); }
  };

  const handleRoleSelect = async (r) => {
    setLoading(true);
    try {
      await api.selectRole(r);
      setRole(r);
      await refresh();
      setStep("onboarding");
    } catch (err) {
      toast.error("Failed to set role");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] text-white relative overflow-hidden">
      <div className="absolute inset-0 dots-bg opacity-40"/>
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#2563EB]/20 blur-[120px] rounded-full"/>
      <div className="relative max-w-md mx-auto px-6 py-12 min-h-screen flex flex-col justify-center">
        <a href="/" className="flex items-center gap-2 mb-8">
          <div className="h-11 w-11 rounded-lg bg-white p-1 flex items-center justify-center shadow-md shadow-blue-500/20"><img src="/logo.png" className="h-full w-full object-contain" alt=""/></div>
          <div className="font-display font-bold"><span className="text-white">CAMPUS</span> <span className="text-[#2563EB]">SENIOR</span></div>
        </a>

        <AnimatePresence mode="wait">
          {step === "form" && (
            <motion.div key="form" initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-20}} className="glass rounded-2xl p-7">
              <h1 className="font-display text-2xl font-bold mb-1">{mode === "login" ? "Welcome back" : "Create your account"}</h1>
              <p className="text-slate-400 text-sm mb-6">{mode === "login" ? "Log in to continue" : "Start your college discovery journey"}</p>

              <form onSubmit={mode === "login" ? handleLogin : handleRegister} className="space-y-3">
                {mode === "register" && (
                  <Field label="Full Name" value={form.full_name} onChange={(v)=>setForm({...form, full_name:v})} required testid="auth-name"/>
                )}
                <Field label="Email" type="email" value={form.email} onChange={(v)=>setForm({...form, email:v})} required testid="auth-email"/>
                {mode === "register" && (
                  <Field label="Phone (+91)" value={form.phone} onChange={(v)=>setForm({...form, phone:v})} required placeholder="+919876543210" testid="auth-phone"/>
                )}
                <Field label="Password" type="password" value={form.password} onChange={(v)=>setForm({...form, password:v})} required testid="auth-password"/>
                {mode === "register" && (
                  <div>
                    <label className="text-xs text-slate-400 mb-1.5 block">Verification method</label>
                    <div className="grid grid-cols-2 gap-2">
                      {["phone","email"].map(m=>(
                        <button key={m} type="button" onClick={()=>setForm({...form, verification_method:m})} className={`px-3 py-2 rounded-lg text-sm border ${form.verification_method===m?"bg-[#2563EB] border-[#2563EB] text-white":"border-white/10 text-slate-300"}`}>{m === "phone" ? "Phone OTP" : "Email OTP"}</button>
                      ))}
                    </div>
                  </div>
                )}
                <button data-testid="auth-submit" disabled={loading} className="w-full py-3 rounded-full bg-[#F59E0B] text-[#0F172A] font-bold hover:bg-amber-400 disabled:opacity-50 transition mt-2">{loading?"Please wait...":(mode === "login" ? "Log In" : "Create Account")}</button>
              </form>
              <div className="mt-5 text-center text-sm text-slate-400">
                {mode === "login" ? "Don't have an account? " : "Already have an account? "}
                <button onClick={()=>setMode(mode === "login" ? "register" : "login")} className="text-[#60A5FA] font-semibold hover:underline" data-testid="auth-toggle-mode">{mode === "login" ? "Sign up" : "Log in"}</button>
              </div>
            </motion.div>
          )}

          {step === "otp" && (
            <motion.div key="otp" initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} className="glass rounded-2xl p-7">
              <h1 className="font-display text-2xl font-bold mb-1">Verify your account</h1>
              <p className="text-slate-400 text-sm mb-6">Enter the 6-digit OTP sent to your {form.verification_method}. <span className="text-amber-400">(dev: use 123456)</span></p>
              <form onSubmit={handleVerifyOtp} className="space-y-4">
                <input data-testid="otp-input" value={otp} onChange={(e)=>setOtp(e.target.value)} maxLength={6} className="w-full bg-[#1E293B] border border-white/10 rounded-lg px-4 py-4 text-center text-2xl tracking-[0.5em] font-display" placeholder="• • • • • •" required/>
                <button data-testid="otp-verify" disabled={loading} className="w-full py-3 rounded-full bg-[#F59E0B] text-[#0F172A] font-bold disabled:opacity-50">Verify</button>
              </form>
            </motion.div>
          )}

          {step === "role" && (
            <motion.div key="role" initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} className="space-y-4">
              <h1 className="font-display text-3xl font-bold text-center">Who are you?</h1>
              <p className="text-slate-400 text-center text-sm">Pick the role that best describes you</p>
              <button data-testid="role-student" disabled={loading} onClick={()=>handleRoleSelect("student")} className="w-full glass rounded-2xl p-6 text-left hover:border-[#2563EB]/60 hover:-translate-y-1 transition">
                <Search className="text-[#60A5FA] mb-3" size={28}/>
                <div className="font-display text-xl font-bold">I'm looking for a college</div>
                <div className="text-slate-400 text-sm mt-1">Chat with verified Campus Ambassadors to make your decision</div>
              </button>
              <button data-testid="role-ambassador" disabled={loading} onClick={()=>handleRoleSelect("campus_ambassador")} className="w-full glass rounded-2xl p-6 text-left hover:border-amber-400/60 hover:-translate-y-1 transition">
                <GraduationCap className="text-amber-400 mb-3" size={28}/>
                <div className="font-display text-xl font-bold">I'm currently in college</div>
                <div className="text-slate-400 text-sm mt-1">Become a Campus Ambassador and help students choose their future</div>
              </button>
            </motion.div>
          )}

          {step === "onboarding" && role === "student" && <StudentOnboarding onDone={()=>nav(next || "/dashboard/student")}/>}
          {step === "onboarding" && role === "campus_ambassador" && <AmbassadorOnboarding onDone={()=>nav("/dashboard/ambassador")}/>}
        </AnimatePresence>
      </div>
    </div>
  );
}

function Field({label, value, onChange, type="text", required, placeholder, testid}) {
  return (
    <div>
      <label className="text-xs text-slate-400 mb-1.5 block">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={(e)=>onChange(e.target.value)} required={required} placeholder={placeholder} className="w-full bg-[#1E293B] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:border-[#2563EB] focus:ring-1 focus:ring-[#2563EB] outline-none transition"/>
    </div>
  );
}

function StudentOnboarding({ onDone }) {
  const [f, setF] = useState({ name:"", state:"", stream:"PCM", percentage:75, preferred_states:[], budget_range:"₹1-3 Lakh" });
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  useEffect(()=>{ if (user) setF(x=>({...x, name: user.full_name})); }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.saveStudentProfile(f); toast.success("Profile saved!"); onDone(); }
    catch { toast.error("Failed to save profile"); }
    finally { setLoading(false); }
  };

  const togglePref = (s) => setF({...f, preferred_states: f.preferred_states.includes(s) ? f.preferred_states.filter(x=>x!==s) : [...f.preferred_states, s]});

  return (
    <motion.form initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} onSubmit={submit} className="glass rounded-2xl p-6 space-y-3">
      <h1 className="font-display text-xl font-bold mb-1">Tell us about you</h1>
      <p className="text-slate-400 text-xs mb-3">We'll personalize college recommendations</p>
      <Field label="Full name" value={f.name} onChange={v=>setF({...f, name:v})} required testid="onb-name"/>
      <div>
        <label className="text-xs text-slate-400 mb-1.5 block">State</label>
        <select data-testid="onb-state" value={f.state} onChange={(e)=>setF({...f, state:e.target.value})} required className="w-full bg-[#1E293B] border border-white/10 rounded-lg px-4 py-3 text-white"><option value="">Select State</option>{STATES.map(s=><option key={s}>{s}</option>)}</select>
      </div>
      <div>
        <label className="text-xs text-slate-400 mb-1.5 block">Stream</label>
        <div className="grid grid-cols-5 gap-1.5">{["PCM","PCB","Commerce","Arts","Other"].map(s=> <button key={s} type="button" onClick={()=>setF({...f,stream:s})} className={`px-2 py-2 rounded-lg text-xs border ${f.stream===s?"bg-[#2563EB] border-[#2563EB]":"border-white/10 text-slate-300"}`}>{s}</button>)}</div>
      </div>
      <Field label="12th Percentage" type="number" value={f.percentage} onChange={v=>setF({...f, percentage:parseFloat(v||0)})} required testid="onb-percentage"/>
      <div>
        <label className="text-xs text-slate-400 mb-1.5 block">Preferred states (multi-select)</label>
        <div className="flex flex-wrap gap-1.5 max-h-32 overflow-auto">{STATES.slice(0,15).map(s=><button key={s} type="button" onClick={()=>togglePref(s)} className={`text-xs px-2.5 py-1 rounded-full border ${f.preferred_states.includes(s)?"bg-[#2563EB] border-[#2563EB]":"border-white/10 text-slate-300"}`}>{s}</button>)}</div>
      </div>
      <div>
        <label className="text-xs text-slate-400 mb-1.5 block">Budget per year</label>
        <select data-testid="onb-budget" value={f.budget_range} onChange={e=>setF({...f, budget_range:e.target.value})} className="w-full bg-[#1E293B] border border-white/10 rounded-lg px-4 py-3 text-white">{["Under ₹1 Lakh","₹1-3 Lakh","₹3-5 Lakh","Above ₹5 Lakh"].map(b=><option key={b}>{b}</option>)}</select>
      </div>
      <button data-testid="onb-submit" disabled={loading} className="w-full py-3 rounded-full bg-[#F59E0B] text-[#0F172A] font-bold disabled:opacity-50">{loading?"Saving...":"Continue to Dashboard"}</button>
    </motion.form>
  );
}

function AmbassadorOnboarding({ onDone }) {
  const [f, setF] = useState({ full_name:"", college_name:"", course:"", year:"1st", bio:"" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { user } = useAuth();
  useEffect(()=>{ if (user) setF(x=>({...x, full_name: user.full_name})); }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await api.saveAmbassadorProfile(f); setSubmitted(true); toast.success("Application submitted!"); }
    catch { toast.error("Failed to submit"); }
    finally { setLoading(false); }
  };

  const uploadId = async (file) => {
    if (!file) return;
    if (file.size > 500*1024) { toast.error(`File too large (${Math.round(file.size/1024)}KB). Max 500KB. Compress at iloveimg.com`); return; }
    try { await api.uploadId(file); toast.success("ID uploaded"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  if (submitted) return (
    <motion.div initial={{opacity:0,scale:0.95}} animate={{opacity:1,scale:1}} className="glass rounded-2xl p-7 text-center">
      <div className="w-16 h-16 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center mx-auto mb-4 text-3xl">✓</div>
      <h2 className="font-display text-2xl font-bold mb-2">Application submitted!</h2>
      <p className="text-slate-400 text-sm">Our team will verify your college ID within 24-48 hours. You'll receive an SMS once approved.</p>
      <button onClick={onDone} className="mt-6 w-full py-3 rounded-full bg-[#2563EB] hover:bg-blue-600 font-bold">Go to Dashboard</button>
    </motion.div>
  );

  return (
    <motion.form initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} onSubmit={submit} className="glass rounded-2xl p-6 space-y-3">
      <h1 className="font-display text-xl font-bold mb-1">Apply as Campus Ambassador</h1>
      <p className="text-slate-400 text-xs mb-3">Step 1 of 2 · Tell us about your college</p>
      <Field label="Full name" value={f.full_name} onChange={v=>setF({...f, full_name:v})} required/>
      <Field label="College name" placeholder="e.g. KIIT University, Bhubaneswar" value={f.college_name} onChange={v=>setF({...f, college_name:v})} required/>
      <Field label="Course / Branch" placeholder="e.g. B.Tech Computer Science" value={f.course} onChange={v=>setF({...f, course:v})} required/>
      <div>
        <label className="text-xs text-slate-400 mb-1.5 block">Year</label>
        <div className="grid grid-cols-5 gap-1.5">{["1st","2nd","3rd","4th","5th"].map(y=><button key={y} type="button" onClick={()=>setF({...f, year:y})} className={`px-2 py-2 rounded-lg text-xs border ${f.year===y?"bg-[#2563EB] border-[#2563EB]":"border-white/10 text-slate-300"}`}>{y}</button>)}</div>
      </div>
      <div>
        <label className="text-xs text-slate-400 mb-1.5 block">Short bio <span className="text-slate-600">({f.bio.length}/200)</span></label>
        <textarea maxLength={200} value={f.bio} onChange={e=>setF({...f, bio:e.target.value})} required placeholder="Tell students what topics you can help them with..." className="w-full bg-[#1E293B] border border-white/10 rounded-lg px-4 py-3 text-white min-h-[80px] outline-none focus:border-[#2563EB]"/>
      </div>
      <div>
        <label className="text-xs text-slate-400 mb-1.5 block">Upload College ID (JPG/PNG/PDF, max 500KB)</label>
        <label className="flex items-center justify-center gap-2 w-full py-4 rounded-lg border-2 border-dashed border-white/15 cursor-pointer hover:border-[#2563EB]">
          <Upload size={16}/><span className="text-sm text-slate-300">Click to upload</span>
          <input type="file" accept="image/jpeg,image/png,application/pdf" onChange={(e)=>uploadId(e.target.files?.[0])} className="hidden"/>
        </label>
        <p className="text-[10px] text-slate-500 mt-1">Tip: Compress images free at iloveimg.com</p>
      </div>
      <button disabled={loading} className="w-full py-3 rounded-full bg-[#F59E0B] text-[#0F172A] font-bold disabled:opacity-50">{loading?"Submitting...":"Submit Application"}</button>
    </motion.form>
  );
}
